// @ts-ignore
import MapboxDraw, {
  DrawActionableState,
  DrawCustomMode,
  DrawCustomModeThis, DrawFeature, DrawMode,
} from "@mapbox/mapbox-gl-draw";
import {BBox, Feature, GeoJSON, Geometry, LineString, MultiPoint, MultiPolygon, Point, Polygon, Position} from "geojson";
import {SnapMode} from "../snap";
import {MapController} from "../map-controller";
import {LayerGeometry} from "../../document/model/layer";
import {MapEvent} from "../../document/model/map";
import {DrawIconOptions} from "../interaction/abstract-interaction";

export class DrawActionDefinition {
  constructor() {
  }
}

export interface CustomDrawOptions {
  geometries: LayerGeometry[];
}

export interface AIMSetupOptions {
  parentFeatureId: string;
  originalLocation: Position;
}

/**
 * Base proxy for Mapbox GL Draw mode
 */
export class CustomDrawMode implements DrawCustomModeThis, DrawCustomMode<CustomDrawOptions> {
  _ctx: {
    store: any,
    api: any,
    container: any,
    events: any,
    map: any,
    options: any,
    setup: any,
    ui: any
  };
  proxy: DrawCustomMode & DrawCustomModeThis;
  drawConfig: MapboxDraw.MapboxDrawOptions;

  constructor(
    public map: mapboxgl.Map,
    public baseDrawMode: DrawCustomMode | undefined = undefined
  ) {
    this.proxy = ProxyMode(this, baseDrawMode) as any;
  }

  getProxy(): DrawCustomMode {
    return this.proxy;
  }

  setSelected(features?: string | string[]): void {
    this.proxy.setSelected(features);
  }

  setSelectedCoordinates(coords: Array<{coord_path: string; feature_id: string}>): void {
    this.proxy.setSelectedCoordinates(coords);
  }

  getSelected(): DrawFeature[] {
    return this.proxy.getSelected();
  }

  getSelectedIds(): string[] {
    return this.proxy.getSelectedIds();
  }

  isSelected(id: string): boolean {
    return this.proxy.isSelected(id);
  }

  getFeature(id: string): DrawFeature {
    return this.proxy.getFeature(id);
  }

  select(id: string): void {
    this.proxy.select(id);
  }

  delete(id: string): void {
    this.proxy.delete(id);
  }

  deleteFeature(id: string | string[], opts?: any): void {
    this.proxy.deleteFeature(id as any, opts);
  }

  addFeature(feature: DrawFeature): void {
    this.proxy.addFeature(feature);
  }

  clearSelectedFeatures(): void {
    this.proxy.clearSelectedFeatures();
  }

  clearSelectedCoordinates(): void {
    this.proxy.clearSelectedCoordinates();
  }

  setActionableState(actionableState: DrawActionableState): void {
    this.proxy.setActionableState(actionableState);
  }

  changeMode(mode: DrawMode | string, opts?: object, eventOpts?: object): void {
    this.proxy.changeMode(mode as DrawMode, opts, eventOpts);
  }

  updateUIClasses(opts: object): void {
    this.proxy.updateUIClasses(opts);
  }

  activateUIButton(name?: string): void {
    this.proxy.activateUIButton(name);
  }

  featuresAt(event: Event, bbox: BBox, bufferType: 'click' | 'tap'): DrawFeature[] {
    return this.proxy.featuresAt(event, bbox, bufferType);
  }

  newFeature(geojson: GeoJSON): DrawFeature {
    return this.proxy.newFeature(geojson);
  }

  isInstanceOf(type: string, feature: object): boolean {
    return this.proxy.isInstanceOf(type, feature);
  }

  doRender(id: string): void {
    this.proxy.doRender(id);
  }

  toDisplayFeatures(state: any, geojson: GeoJSON, display: (geojson: GeoJSON) => void): void {
    throw new Error('Must be implemented');
  }

  _inheritedMethods(): string[] {
    return [
      "setup",
      "onSetup",
      "onDrag",
      "onClick",
      "onMouseMove",
      "onMouseDown",
      "onMouseUp",
      "onMouseOut",
      "onKeyUp",
      "onKeyDown",
      "onTouchStart",
      "onTouchMove",
      "onTouchEnd",
      "onTap",
      "onStop",
      "onTrash",
      "onCombineFeature",
      "onUncombineFeature",
    ];
  }
}

/**
 * Base for all draw modes
 */
export abstract class AbstractDrawMode extends CustomDrawMode {

  static selectionAllowed: boolean = false;
  static selectionRequired: boolean = false;
  static multiSelection: boolean = false;
  //static showVertexLength: boolean = false;
  static supportedSnapModes: SnapMode[] = [];

  setup(options: any): any {
    return {};
  }

  getSupportedSnapModes(): SnapMode[] {
    return (<typeof AbstractDrawMode>this.constructor).supportedSnapModes;
  }

  setSupportedSnapModes(snapModes: SnapMode[]): void {
    (<typeof AbstractDrawMode>this.constructor).supportedSnapModes = snapModes;
    this.map.fire('draw.snap_mode_changed');
  }

  isSnapModeSupported(mode: SnapMode): boolean {
    return (<typeof AbstractDrawMode>this.constructor).supportedSnapModes.indexOf(mode) >= 0;
  }

  onSnapModeChange(mode: SnapMode, state: any): void {
  }

  isTraceModeAccepted(): boolean {
    return false;
  }

  isSelectionAllowed(): boolean {
    return (<typeof AbstractDrawMode>this.constructor).selectionAllowed === true;
  }

  isSelectionRequired(): boolean {
    return (<typeof AbstractDrawMode>this.constructor).selectionRequired === true;
  }

  isMultiSelection(): boolean {
    return (<typeof AbstractDrawMode>this.constructor).multiSelection === true;
  }

  onSetup(options: any): any {
    options.geometries = this.controller().interaction.getFocused();
    let state = this.setup(options);
    if (this.baseDrawMode) {
      state = {...state, ...this.baseDrawMode.onSetup.apply(this, [options])};
    }

    this.map.fire('draw.mode_init', {mode: this, state});

    return state;
  }

  setupAutoAIM(options: Partial<AIMSetupOptions> = {}) {
    let aim = DrawGLUtil.createIcon(
      options.originalLocation ?? [0, 0],
      'auto-aim',
      {
        id: 'aim',
        parent: options.parentFeatureId,
      }
    );

    this._ctx.store.add(aim);

    return {aim, cursorPosition: undefined, snapFeature: undefined};
  }

  autoAIM(state, e, target) {
    e.lngLat.lng = target[0];
    e.lngLat.lat = target[1];


    state.aim.geometry.coordinates = target;
    this._ctx.store.add(state.aim);
    this._ctx.store.render();
  }

  isVertex(e, features: Feature[] | undefined = undefined): boolean {
    if (features === undefined) {
      features = this.map.queryRenderedFeatures(e.point);
    }
    return features.filter(feature => {
      return feature.properties.meta === 'vertex'
    }).length > 0;
  }

  isAIM(e, features: Feature[] | undefined = undefined): boolean {
    if (features === undefined) {
      features = this.map.queryRenderedFeatures(e.point);
    }
    return features.filter(feature => {
      return feature.properties.id === 'aim'
    }).length > 0;
  }

  removeAutoAIM(state) {
    if (state.aim) {
      this.deleteFeature(state.aim.id);
    }
  }

  controller(): MapController {
    return (this.map as any)._controller;
  }

  createLayerGeometries(features: Feature[]): void {
    this.map.fire(MapEvent.LayerGeometryCreate, {features: features});
  }

  attemptUpdateLayerGeometry(layerGeometry: LayerGeometry, feature: Feature, confirmCallback, closeCallback) {
    // @ts-ignore
    if (confirm(this.controller().translate("Do you want to save geometry changes?"))) {
      confirmCallback();
    } else {
      closeCallback();
    }
    /*this.controller().$dialog.open(
      MapConfirmChanges,
      {
        props: {
          header: this.controller().$t("Do you want to save geometry changes?"),
          style: {
            width: "450px",
          },
          modal: true
        },
        onClose: async (options) => {
          switch (options.type) {
            case 'config-close':
              if (!options.data?.confirm) {
                return;
              }
              confirmCallback();
              break;
            default:
              closeCallback();
              break;
          }
        }
      }
    );*/
  }

  reset(state) {
  }

  save(state) {
  }

  updateLayerGeometry(layerGeometry: LayerGeometry, feature: Feature) {
    this.map.fire(MapEvent.LayerGeometryUpdate, {layerGeometry: layerGeometry, feature});
  }

  tick() {
    this.map.fire('tick');
  }

  contextMenu(state: any): any[] {
    return [];
  }

  _inheritedMethods(): string[] {
    return [
      ...super._inheritedMethods(),
      'getSupportedSnapModes',
      'setSupportedSnapModes',
      'isSnapModeSupported',
      'onSnapModeChange',
      'isTraceModeAccepted',
      'isSelectionAllowed',
      'isSelectionRequired',
      'isMultiSelection',
      'setupAutoAIM',
      'autoAIM',
      'removeAutoAIM',
      'controller',
      'createLayerGeometries',
      'updateLayerGeometry',
      'attemptUpdateLayerGeometry',
      'tick',
      'contextMenu',
      'isVertex',
      'isAIM',
      'save',
      'reset'
    ];
  }
}


/**
 * Generates a compatible drawMode proxy for Mapbox GL Draw
 * @param {CustomDrawMode} interaction
 * @param {DrawCustomMode} baseMode
 * @constructor
 */
function ProxyMode(interaction: CustomDrawMode, baseMode: DrawCustomMode | undefined = undefined): DrawCustomMode {
  let mode: any | DrawCustomMode = {};

  if (baseMode) {
    Object.getOwnPropertyNames(baseMode).forEach(baseModeMethod => {
      mode[baseModeMethod] = function (...args) {
        return baseMode[baseModeMethod].apply(this, args);
      }
    })
  }

  let methods = interaction._inheritedMethods();

  methods.forEach(interfaceMethod => {
    if (typeof interaction[interfaceMethod] !== 'function') {
      return;
    }
    mode[interfaceMethod] = function (...args) {
      return interaction[interfaceMethod].apply(this, args);
    };
  });

  mode['toDisplayFeatures'] = function (...args): void {
    try {
      interaction.toDisplayFeatures.apply(this, args)
    } catch (e) {
      if (interaction.baseDrawMode && interaction.baseDrawMode['toDisplayFeatures']) {
        if (e.message !== 'Must be implemented') {
          console.error(e);
        }
        interaction.baseDrawMode?.toDisplayFeatures.apply(this, args);
        return;
      }
      throw e;
    }
  }

  mode.baseDrawMode = baseMode;

  return mode;
}

export interface DrawFeatureOptions {
  id: string;
  internal: boolean;
  parent: string;
  coord_path: string;
  active: boolean;
  meta: string;
  label: string;
  icon: string;
}

export class DrawGLUtil {
  static createFeature(geometry: Geometry, opts: Partial<DrawFeatureOptions>): Feature {
    opts = {
      internal: true,
      active: false,
      ...opts
    };

    let feature = {
      type: 'Feature',
      id: opts.id ?? undefined,
      geometry,
      properties: {...opts}
    }

    if (opts.internal) {
      (feature as any).internal = (mode) => {
        return {...feature};
      }

      (feature as any).toGeoJSON = function () {
        return {...feature};
      }
    }

    return feature as Feature;
  }

  static createLabel(position: Position, label: string, opts: Partial<DrawFeatureOptions> = {}): Feature<Point> {
    return this.createFeature({type: 'Point', coordinates: position}, {
      ...opts,
      meta: 'label',
      label
    }) as Feature<Point>;
  }

  static createIcon(position: Position, icon: string, opts: Partial<DrawFeatureOptions & DrawIconOptions> = {}): Feature<Point> {
    let anchor = opts['icon-anchor'] ?? 'center';
    let perspective = opts['icon-flat'] ? 'flat' : '3d';

    delete opts['icon-flat'];
    delete opts['icon-anchor'];

    return this.createFeature(
      {type: 'Point', coordinates: position},
      {...opts, meta: `icon-${perspective}-${anchor}`, icon}
    ) as Feature<Point>;
  }

  static createPoint(position: Position, opts: Partial<DrawFeatureOptions> = {}): Feature<Point> {
    return this.createFeature(
      {type: "Point", coordinates: position},
      {
        ...opts,
        meta: 'feature'
      }) as Feature<Point>;
  }

  static createMultiPoint(points: Position[], opts: Partial<DrawFeatureOptions> = {}): Feature<MultiPoint> {
    return this.createFeature(
      {type: "MultiPoint", coordinates: points},
      {
        ...opts,
        meta: 'feature'
      }) as Feature<MultiPoint>;
  }

  static createLineString(points: Position[], opts: Partial<DrawFeatureOptions> = {}): Feature<LineString> {
    return this.createFeature(
      {type: "LineString", coordinates: points},
      {
        ...opts,
        meta: 'feature'
      }) as Feature<LineString>;
  }

  static createPolygon(points: Position[], opts: Partial<DrawFeatureOptions> = {}): Feature<Polygon> {
    return this.createFeature(
      {type: "Polygon", coordinates: [points]},
      {...opts, meta: 'feature'}
    ) as Feature<Polygon>;
  }

  static createMultiPolygon(points: Position[][], opts: Partial<DrawFeatureOptions> = {}): Feature<MultiPolygon> {
    return this.createFeature(
      {
        type: "MultiPolygon",
        coordinates: points.map(set => [set])
      },
      {...opts, meta: 'feature'}
    ) as any;
  }
}
